import * as React from "react";
import ApplicationStatus from "../../applications/components/ApplicationStatus";
import NumberFormat from "react-number-format";
import moment from "moment-timezone";
import AddFavoriteCandidate from "../../favorites/components/AddFavoriteCandidate";
import AddBlockCandidate from "../../favorites/components/AddBlockCandidate";
import { ApplicantsNotes } from "../../notes/components/ProfileNotes";
import { ApplicantsContext } from "./ManageApplicants";
import SBSendMessage from "./SBChat/SBSendMessage";
import RpoManagedNotice from "./RpoManagedNotice";
import { InviteProfile } from "../../profiles/components/InviteProfile";
import type { ApplicantRecord, ProfileNote } from "../../../shared/types/domainModels";

// Types
interface Job {
  id: number;
  position_type: string;
  [key: string]: unknown;
}

interface ApplicantProps {
  applicant: ApplicantRecord;
  favorites: number[];
  authenticity_token: string;
  notes: ProfileNote[];
  load_notes: () => void;
  loaded: boolean;
  timezone: string;
  position_type?: string;
  job?: Job;
  isHidden?: boolean;
  requested: number[];
  accepted: number[];
  declined: number[];
}

interface ApplicantsProps {
  applicants: ApplicantRecord[];
  favorites: number[];
  authenticity_token: string;
  notes: ProfileNote[];
  load_notes: () => void;
  loaded: boolean;
  timezone: string;
  position_type?: string;
  job?: Job;
  isHiddenList?: boolean;
  requested: number[];
  accepted: number[];
  declined: number[];
}

const Applicant = ({
  applicant,
  favorites,
  authenticity_token,
  notes,
  load_notes,
  loaded,
  timezone,
  position_type,
  job,
  isHidden,
  requested,
  accepted,
  declined,
}: ApplicantProps) => {
  const { profile } = applicant;
  const application_date = moment(applicant.created_at)
    .tz(timezone)
    .format("llll");
  const updated_date = moment(applicant.updated_at).tz(timezone).format("llll");
  const phone_mask = profile.phone && profile.phone.length > 10 ? "+# (###) ###-####" : "(###) ###-####";
  const profile_url = `/profiles/${profile.id}`;

  const { hideProfile, unhideProfile, updateStatus } = React.useContext(ApplicantsContext);

  const applied_string = applicant.withdrawn
    ? `Withdrawn ${updated_date}`
    : `Applied ${application_date}`;

  const [notesExpanded, setNotesExpanded] = React.useState(false);

  return (
    <div id={`application-${applicant.id}`} style={{ marginBottom: '1.25rem', scrollMarginTop: '80px' }} className="profile-list-modern">
      <div style={{
        background: 'white',
        border: '1px solid #e8ecef',
        borderRadius: '0.75rem',
        padding: 0,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02), 0 1px 2px rgba(0, 0, 0, 0.03)',
        transition: 'all 0.3s ease',
        overflow: 'hidden'
      }}>
        {/* Top Color Stripe */}
        <div style={{
          height: '4px',
          background: 'linear-gradient(90deg, #085ec8 0%, #176fc9 50%, #71ad08 100%)',
          borderRadius: '0.75rem 0.75rem 0 0'
        }}></div>

        {/* Main Content Area */}
        <div style={{ padding: '1.25rem' }}>
          {/* Header Section with Avatar and Name */}
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1rem',
            flexWrap: 'wrap'
          }}>
            {/* Avatar */}
            <a href={profile_url} target="_blank" rel="noopener noreferrer">
              <img
                src={profile.avatar_url}
                className="rounded-circle"
                alt={profile.fullname}
                style={{
                  width: '60px',
                  height: '60px',
                  objectFit: 'cover',
                  border: '3px solid #e8ecef',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.border = '3px solid #085ec8'}
                onMouseOut={(e) => e.currentTarget.style.border = '3px solid #e8ecef'}
              />
            </a>

            {/* Name and Details */}
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '0.5rem',
                flexWrap: 'wrap'
              }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  margin: 0
                }}>
                  <a
                    href={`/profiles/${profile.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#085ec8',
                      textDecoration: 'none',
                      transition: 'color 0.3s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#71ad08'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#085ec8'}
                  >
                    {profile.fullname}
                  </a>
                </h3>
                <AddFavoriteCandidate
                  profile={profile}
                  favorited_init={favorites.includes(profile.id)}
                  authenticity_token={authenticity_token}
                  expandDirection="left"
                />
                <AddBlockCandidate
                  profile={profile}
                  job_id={job?.id}
                  favorited_init={false}
                  authenticity_token={authenticity_token}
                  blockedCallback={() => {}}
                  expandDirection="left"
                />
              </div>

              {/* Location, Rate, and Application Date */}
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap',
                alignItems: 'center',
                fontSize: '0.875rem',
                color: '#6c757d'
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <i className="fa fa-map-marker" style={{ color: '#085ec8' }}></i>
                  {profile.city}, {profile.state}
                </span>
                {profile.hourly_rate && (
                  <>
                    <span style={{ color: '#e8ecef' }}>|</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <i className="fa fa-money" style={{ color: '#71ad08' }}></i>
                      ${profile.hourly_rate}/hr
                    </span>
                  </>
                )}
                <span style={{ color: '#e8ecef' }}>|</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <i className="fa fa-calendar" style={{ color: '#085ec8' }}></i>
                  {applied_string}
                </span>
              </div>
            </div>

            {/* Action Icons - Compact */}
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'center'
            }}>
              {isHidden ? (
                <button
                  onClick={() => unhideProfile(applicant)}
                  style={{
                    background: '#28a745',
                    border: 'none',
                    borderRadius: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                  onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                  title="Restore applicant"
                >
                  <i className="fa fa-recycle"></i>
                  Restore
                </button>
              ) : (
                <button
                  onClick={() => hideProfile(applicant)}
                  style={{
                    background: 'white',
                    border: '1px solid #dc3545',
                    borderRadius: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    color: '#dc3545',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#dc3545';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.color = '#dc3545';
                  }}
                  title="Delete applicant"
                >
                  <i className="fa fa-trash"></i>
                  Delete
                </button>
              )}
            </div>
          </div>

          {/* Status Dropdown - Side-by-Side Layout */}
          <div style={{
            background: 'rgba(8, 94, 200, 0.03)',
            borderRadius: '0.5rem',
            padding: '0.875rem 1rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <label style={{
              margin: 0,
              color: '#034ba6',
              fontWeight: 600,
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
              flex: '0 0 auto'
            }}>
              <i className="fa fa-tasks" style={{ marginRight: '0.375rem' }}></i>
              Application Status
            </label>
            <div style={{ flex: '1 1 200px', minWidth: '200px', display: 'flex', alignItems: 'center' }}>
              <ApplicationStatus application={applicant} updateStatusFunc={updateStatus} />
            </div>
          </div>

          {/* Contact Actions - Compact */}
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            flexWrap: 'wrap'
          }}>
            {profile.phone && (
              <a
                href={`tel:${profile.phone}`}
                style={{
                  flex: '1 1 180px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  textDecoration: 'none',
                  background: 'white',
                  color: '#085ec8',
                  border: '2px solid #085ec8',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 6px rgba(8, 94, 200, 0.12)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#085ec8';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 10px rgba(8, 94, 200, 0.2)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#085ec8';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(8, 94, 200, 0.12)';
                }}
              >
                <i className="fa fa-phone"></i>
                <NumberFormat
                  value={profile.phone}
                  displayType="text"
                  type="text"
                  format={phone_mask}
                  mask="_"
                />
              </a>
            )}
            {applicant.rpo_managed ? (
              <div style={{ flex: '1 1 180px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.75rem 1rem' }}>
                <RpoManagedNotice />
              </div>
            ) : (
              <div style={{ flex: '1 1 180px' }}>
                <SBSendMessage profileId={profile.id.toString()} />
              </div>
            )}
            {position_type === 'temp' && job && !applicant.rpo_managed && (
              <div style={{ flex: '1 1 180px', display: 'flex' }}>
                <InviteProfile
                  profile={{
                    id: profile.id,
                    first_name: profile.fullname?.split(' ')[0] || '',
                    last_name: profile.fullname?.split(' ').slice(1).join(' ') || '',
                    city: profile.city || '',
                    state: profile.state || '',
                    zip: '',
                    image: profile.avatar_url || '',
                    url: `/profiles/${profile.id}`,
                    compensation: profile.hourly_rate || 0,
                    job_types: [],
                    skills: [],
                    position_types: []
                  }}
                  job={job}
                  requested={requested}
                  accepted={accepted}
                  declined={declined}
                  authenticity_token={authenticity_token}
                  fullWidth={true}
                />
              </div>
            )}
          </div>
        </div>

        {/* Notes Section - Collapsible */}
        <div style={{
          borderTop: '1px solid #e8ecef',
          background: 'linear-gradient(to bottom, rgba(8, 94, 200, 0.02), rgba(23, 111, 201, 0.01))'
        }}>
          {/* Notes Toggle Header */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setNotesExpanded(!notesExpanded);
            }}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.875rem 1.25rem',
              background: 'linear-gradient(135deg, #085ec8, #176fc9)',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.875rem'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #0751a8, #135a9f)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #085ec8, #176fc9)'}
          >
            <span>
              <i className="fa fa-sticky-note" style={{ marginRight: '0.5rem' }}></i>
              Notes {notes.length > 0 && `(${notes.length})`}
            </span>
            <i className={`fa fa-chevron-${notesExpanded ? 'up' : 'down'}`} style={{ fontSize: '0.75rem' }}></i>
          </button>

          {/* Notes Content - Collapsible */}
          {notesExpanded && (
            <div style={{
              padding: '0 1.25rem 1.25rem 1.25rem',
              animation: 'slideDown 0.2s ease-out'
            }}>
              <ApplicantsNotes
                profile_id={profile.id}
                notes={notes}
                authenticity_token={authenticity_token}
                load_notes={load_notes}
                loaded={loaded}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Applicants = ({
  applicants,
  favorites,
  authenticity_token,
  notes,
  load_notes,
  loaded,
  timezone,
  position_type,
  job,
  isHiddenList,
  requested = [],
  accepted = [],
  declined = [],
}: ApplicantsProps) => {
  return (
    <>
      {applicants
        .sort((a, b) => {
          // Sort withdrawn applicants to the end
          if (a.withdrawn && !b.withdrawn) return 1;
          if (!a.withdrawn && b.withdrawn) return -1;
          return 0;
        })
        .map(applicant => (
          <Applicant
            key={applicant.id}
            applicant={applicant}
            favorites={favorites}
            authenticity_token={authenticity_token}
            load_notes={load_notes}
            loaded={loaded}
            timezone={timezone}
            position_type={position_type}
            job={job}
            notes={notes.filter((note) => applicant.profile.id === note.profile_id)}
            isHidden={isHiddenList}
            requested={requested}
            accepted={accepted}
            declined={declined}
          />
        ))}
    </>
  );
};

export default Applicants;
